/**
 * Holds all of the state and stuff for the front end configuration form.
 * In the current incarnation of the application, there is only ever
 * one form and the state persists even when the form is not rendered, e.g.
 * changing tabs
 */
import { UserGeneratedConfigService } from 'app/config/user_generated_config.service';
import { EventRegistry } from 'app/shared/events/event_registry';
import { IFormFieldModel } from 'app/overview/config/configuration_tab/general/frontend_config/IFormFieldModel';

export enum FrontEndConfigFormField {
    REMOVE_BASE_BING_LAYER = 'removeBaseBingLayer',
    LIVE_LATENCY = 'liveLatency',
    IMAGERY_LAYERS = 'imageryLayers',
    TERRAIN_PROVIDERS = 'terrainProviders',
    HEATMAP_LIMIT = 'heatMapDataLimit',
    HEATMAP_CHUNK_SIZE = 'heatMapChunkSize',
    CONNECTION_OUTLINE = 'connectionOutline',
    THROUGHPUT_AS_BITS = 'throughputAsBits',
    FEATURE_LAYERS = 'featureLayers',
    BASE_GLOBE_COLOR = 'baseGlobeColor',
}

interface FormFieldState {
    data: any;
}

type onFieldChangeCallback = (state: FormFieldState) => void;

export class FrontEndConfigFormModel {
    private _userGeneratedConfigService: UserGeneratedConfigService;

    private _eventRegistriesByField: { [field: string]: EventRegistry };

    // Yep
    private _configDataBeingEdited: any;

    private _innerFormFields: IFormFieldModel[];

    constructor(userGeneratedConfigService: UserGeneratedConfigService) {
        this._innerFormFields = [];
        this._eventRegistriesByField = {};
        this._userGeneratedConfigService = userGeneratedConfigService;
        this._userGeneratedConfigService.onConfigLoad(() => {
            this._configDataBeingEdited = this._userGeneratedConfigService.configData;
            // First time load....send out all relevant events...
            Object.keys(this._configDataBeingEdited).forEach((key) => {
                this._fireOnFieldChangeFor(key, this._configDataBeingEdited[key]);
            });
        });
        this._userGeneratedConfigService.onConfigChange(() => {
            this._configDataBeingEdited = this._userGeneratedConfigService.configData;
            // First time load....send out all relevant events...
            Object.keys(this._configDataBeingEdited).forEach((key) => {
                this._fireOnFieldChangeFor(key, this._configDataBeingEdited[key]);
            });
        });
    }

    /**
     *
     * @param {FrontEndConfigFormField} formField
     * @param value
     */
    handleFormChange(formField: FrontEndConfigFormField, value: any) {
        this._fireOnFieldChangeFor(formField, value);
    }

    /**
     * Save the data to persistent storage somewhere
     * @param {FrontEndConfigFormField} formField
     * @param value
     */
    async saveFormField(formField: FrontEndConfigFormField, value: any): Promise<any> {
        return this._userGeneratedConfigService.updateConfig(value, getFormFieldKey(formField));
    }

    /**
     * This event returns only actually saved data...I think
     * @param {FrontEndConfigFormField} formField
     * @param {onFieldChangeCallback} callback
     * @returns {() => void}
     */
    onFieldChange(formField: FrontEndConfigFormField, callback: onFieldChangeCallback): () => void {
        const key = getFormFieldKey(formField);
        // lazy creation
        let eventReg = this._eventRegistriesByField[key];
        if (!eventReg) {
            eventReg = new EventRegistry();
            this._eventRegistriesByField[key] = eventReg;
        }
        const destroyListener = eventReg.addEventListener(callback);
        // Make an initial callback if possible
        if (this._configDataBeingEdited) {
            callback(this._createOnFieldChangeEventData(this._configDataBeingEdited[formField]));
        }
        return destroyListener;
    }

    addFormFieldModel(formFieldModel: IFormFieldModel) {
        if (this._innerFormFields.includes(formFieldModel)) {
            throw new Error(`double registration of form field model`);
        }
        if (this._filterFormFieldsById(formFieldModel.getId()).length !== 0) {
            throw new Error(
                `attempting to add a form field with duplicate id ${formFieldModel.getId()}`,
            );
        }
        this._innerFormFields.push(formFieldModel);
    }

    removeFormFieldModel(formFieldModel: IFormFieldModel) {
        if (!this._innerFormFields.includes(formFieldModel)) {
            throw new Error(`cant remove form field model that isn't registered`);
        }
        this._innerFormFields = this._innerFormFields.filter((f) => f !== formFieldModel);
    }

    get formFieldCount(): number {
        return this._innerFormFields.length;
    }

    /**
     *
     * @param {string} id the ID of the form field to find
     * @returns {IFormFieldModel<T>} Kind of crappy type safety here, meh
     */
    getFormFieldById<T>(id: string): IFormFieldModel<T> {
        const result = this._filterFormFieldsById(id);
        if (result.length !== 1) {
            throw new Error(`Expect exactly 1 for ${id} but was ${result.length}`);
        }
        return result[0];
    }

    private _filterFormFieldsById(id: string) {
        return this._innerFormFields.filter((f) => f.getId() == id);
    }

    private _getFormFieldEventReg(fieldKey: string): EventRegistry {
        return this._eventRegistriesByField[fieldKey];
    }

    private _fireOnFieldChangeFor(fieldKey: string, value: any) {
        const eventReg = this._getFormFieldEventReg(fieldKey);
        if (eventReg) {
            eventReg.notifyListeners(this._createOnFieldChangeEventData(value));
        }
    }

    private _createOnFieldChangeEventData(fieldValue: any): FormFieldState {
        return {
            data: fieldValue,
        };
    }
}

function getFormFieldKey(formField: FrontEndConfigFormField): string {
    return formField.toString();
}
